import { useState, useEffect } from 'react'
import api from '../../services/api'
import toast from 'react-hot-toast'
import { FileText, CheckCircle, Save, Globe, Users, ExternalLink, PlusCircle, X, Award, TrendingUp, Sparkles, ArrowDownLeft } from 'lucide-react'
import LoadingSpinner from '../../components/LoadingSpinner'

const StatusBadge = ({ status }) => {
  switch (status) {
    case 'VERIFIED':
      return <span className="inline-block px-1.5 py-0.5 text-[10px] font-mono tracking-wider font-semibold text-green-400 bg-green-500/10 border border-green-500/30">VERIFIED</span>
    case 'APPARENT':
      return <span className="inline-block px-1.5 py-0.5 text-[10px] font-mono tracking-wider font-semibold text-blue-400 bg-blue-500/10 border border-blue-500/30">APPARENT</span>
    case 'NOT_FOUND':
      return <span className="inline-block px-1.5 py-0.5 text-[10px] font-mono tracking-wider font-semibold text-red-400 bg-red-500/10 border border-red-500/30">NOT FOUND</span>
    case 'UNVERIFIABLE':
      return <span className="inline-block px-1.5 py-0.5 text-[10px] font-mono tracking-wider font-semibold text-amber-400 bg-amber-500/10 border border-amber-500/30">UNVERIFIABLE</span>
    case 'NOT_APPLICABLE':
      return <span className="inline-block px-1.5 py-0.5 text-[10px] font-mono tracking-wider font-semibold text-cinema-muted bg-white/5 border border-white/10">N/A</span>
    default:
      return <span className="inline-block px-1.5 py-0.5 text-[10px] font-mono tracking-wider text-cinema-muted bg-white/5 border border-white/10">{status}</span>
  }
}

const SubmissionTypeBadge = ({ type }) => {
  if (type === 'in_person') {
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 font-sans text-xs font-medium bg-purple-500/10 text-purple-400 border border-purple-500/25">
        <Users size={12} /> In Person
      </span>
    )
  }
  return (
    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 font-sans text-xs font-medium bg-blue-500/10 text-blue-400 border border-blue-500/25">
      <Globe size={12} /> Online
    </span>
  )
}

export default function Grading() {
  const [submissions, setSubmissions] = useState([])
  const [courses, setCourses] = useState([])
  const [selectedCourse, setSelectedCourse] = useState('')
  const [loading, setLoading] = useState(true)

  // Grade modal
  const [gradingSubmission, setGradingSubmission] = useState(null)
  const [gradeForm, setGradeForm] = useState({ score: '', feedback: '' })

  // AI-010 Phase 1: Review Assist State
  const [reviewLoading, setReviewLoading] = useState(false)
  const [reviewData, setReviewData] = useState(null)
  const [reviewError, setReviewError] = useState(null)
  const [focusArea, setFocusArea] = useState('')

  // Record in-person modal
  const [showInPersonModal, setShowInPersonModal] = useState(false)
  const [courseStudents, setCourseStudents] = useState([])
  const [courseAssignments, setCourseAssignments] = useState([])
  const [inPersonForm, setInPersonForm] = useState({ student_id: '', assignment_id: '', notes: '', score: '', feedback: '' })
  const [savingInPerson, setSavingInPerson] = useState(false)

  // Tab: 'pending' | 'graded' | 'student_grades'
  const [activeTab, setActiveTab] = useState('pending')
  const [studentGrades, setStudentGrades] = useState([])
  const [loadingGrades, setLoadingGrades] = useState(false)

  useEffect(() => { fetchCourses() }, [])
  useEffect(() => { if (selectedCourse) fetchSubmissions() }, [selectedCourse])
  useEffect(() => { if (selectedCourse && activeTab === 'student_grades') fetchStudentGrades() }, [selectedCourse, activeTab])

  const fetchCourses = async () => {
    try {
      const { data } = await api.get('/trainers/courses')
      setCourses(data)
      if (data.length > 0) setSelectedCourse(data[0].id)
    } catch {
      toast.error('Failed to load courses')
      setLoading(false)
    }
  }

  const fetchSubmissions = async () => {
    try {
      setLoading(true)
      const { data } = await api.get(`/trainers/courses/${selectedCourse}/submissions`)
      setSubmissions(data)
    } catch {
      toast.error('Failed to load submissions')
    } finally {
      setLoading(false)
    }
  }

  const fetchStudentGrades = async () => {
    try {
      setLoadingGrades(true)
      const { data } = await api.get(`/trainers/courses/${selectedCourse}/student-grades`)
      setStudentGrades(data)
    } catch {
      toast.error('Failed to load student grades')
    } finally {
      setLoadingGrades(false)
    }
  }

  const handleGradeSubmit = async (e) => {
    e.preventDefault()
    try {
      await api.put(`/trainers/submissions/${gradingSubmission.id}/grade`, gradeForm)
      toast.success('Grade submitted successfully')
      closeGradingModal()
      setGradeForm({ score: '', feedback: '' })
      fetchSubmissions()
    } catch {
      toast.error('Failed to submit grade')
    }
  }

  const handleAiReviewAssist = async () => {
    if (!gradingSubmission) return
    setReviewLoading(true)
    setReviewError(null)
    try {
      const payload = {}
      if (focusArea.trim()) {
        payload.focusArea = focusArea.trim()
      }
      const { data } = await api.post(`/trainers/submissions/${gradingSubmission.id}/review-assist`, payload)
      const reviewObj = data.review || data.data || data
      setReviewData(reviewObj)
      if (data.ai_enhanced === false) {
        toast('Review assistant operating in fallback mode.', { icon: 'ℹ️' })
      } else {
        toast.success('Review observations generated')
      }
    } catch (err) {
      const msg = err.response?.data?.error || 'AI review assistance is currently unavailable. You can continue grading manually.'
      setReviewError(msg)
      toast.error(msg)
    } finally {
      setReviewLoading(false)
    }
  }

  const handleInsertFeedback = (draft) => {
    if (!draft) return
    setGradeForm((prev) => ({
      ...prev,
      feedback: draft,
    }))
    toast.success('Draft inserted into feedback')
  }

  const closeGradingModal = () => {
    setGradingSubmission(null)
    setReviewData(null)
    setReviewError(null)
    setFocusArea('')
  }

  const openInPersonModal = async () => {
    setShowInPersonModal(true)
    setInPersonForm({ student_id: '', assignment_id: '', notes: '', score: '', feedback: '' })
    try {
      const [studentsRes, assignmentsRes] = await Promise.all([
        api.get(`/trainers/courses/${selectedCourse}/students`),
        api.get(`/trainers/courses/${selectedCourse}/assignments`),
      ])
      setCourseStudents(studentsRes.data)
      setCourseAssignments(assignmentsRes.data)
    } catch {
      toast.error('Failed to load course data')
    }
  }

  const handleInPersonSubmit = async (e) => {
    e.preventDefault()
    setSavingInPerson(true)
    try {
      // Step 1: record the in-person submission
      const { data: submissionData } = await api.post('/trainers/submissions/in-person', {
        student_id: inPersonForm.student_id,
        assignment_id: inPersonForm.assignment_id,
        notes: inPersonForm.notes,
      })

      // Step 2: if a score was provided, grade it immediately
      if (inPersonForm.score !== '' && inPersonForm.score !== null) {
        await api.put(`/trainers/submissions/${submissionData.submission.id}/grade`, {
          score: inPersonForm.score,
          feedback: inPersonForm.feedback,
        })
        toast.success('Submission recorded and graded!')
      } else {
        toast.success('In-person submission recorded')
      }

      setShowInPersonModal(false)
      fetchSubmissions()
      if (activeTab === 'student_grades') fetchStudentGrades()
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to record submission')
    } finally {
      setSavingInPerson(false)
    }
  }

  const pendingSubmissions = submissions.filter(s => !s.score)
  const gradedSubmissions = submissions.filter(s => s.score !== null)
  const displayed = activeTab === 'pending' ? pendingSubmissions : gradedSubmissions

  const gradeAccent = (pct) => {
    if (pct === null || pct === undefined) return { text: 'text-cinema-muted', bar: 'bg-white/20' }
    if (pct >= 75) return { text: 'text-green-400', bar: 'bg-green-500' }
    if (pct >= 50) return { text: 'text-cinema-glow', bar: 'bg-cinema-glow' }
    return { text: 'text-red-400', bar: 'bg-red-500' }
  }

  const inputCls = "w-full bg-white/5 border border-white/10 text-white text-sm font-sans px-3 py-2.5 focus:outline-none focus:border-cinema-glow/40 transition-colors placeholder:text-cinema-muted/40"
  const modalOverlay = "fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
  const modalBox = "cinema-glass-strong border border-white/10 rounded-sm w-full max-w-lg max-h-[90vh] overflow-y-auto p-6 space-y-4"

  if (loading && activeTab !== 'student_grades') return <LoadingSpinner label="Loading submissions" />

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="cinema-page-header flex flex-wrap items-start justify-between gap-3">
        <div>
          <span className="cinema-label block mb-0.5">Trainer Portal</span>
          <h1 className="font-display text-xl text-white font-light">Grade Assignments</h1>
        </div>
        <button onClick={openInPersonModal} className="cinema-btn-primary text-xs px-4 py-2.5 min-h-0 flex items-center gap-2">
          <PlusCircle size={13} /> Record In-Person
        </button>
      </div>

      {/* Course selector */}
      <div>
        <label className="cinema-label block mb-2">Select Course</label>
        <select value={selectedCourse} onChange={(e) => setSelectedCourse(e.target.value)} className={`${inputCls} max-w-md`}>
          {courses.map((course) => (
            <option key={course.id} value={course.id} className="bg-gray-900">{course.title}</option>
          ))}
        </select>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-x-8 gap-y-8">
        <div className="cinema-stat">
          <span className="cinema-stat-label">Pending Review</span>
          <span className="cinema-stat-value">{pendingSubmissions.length}</span>
          <span className="cinema-stat-sub">
            {pendingSubmissions.filter(s => s.submission_type === 'in_person').length} in-person · {pendingSubmissions.filter(s => s.submission_type !== 'in_person').length} online
          </span>
        </div>
        <div className="cinema-stat">
          <span className="cinema-stat-label">Graded</span>
          <span className="cinema-stat-value">{gradedSubmissions.length}</span>
          <span className="cinema-stat-sub">
            {gradedSubmissions.filter(s => s.submission_type === 'in_person').length} in-person · {gradedSubmissions.filter(s => s.submission_type !== 'in_person').length} online
          </span>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-white/6">
        {['pending', 'graded', 'student_grades'].map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-5 py-2.5 font-sans text-xs font-medium border-b-2 transition-colors ${
              activeTab === tab ? 'border-cinema-glow text-cinema-glow' : 'border-transparent text-cinema-muted hover:text-white'
            }`}
          >
            {tab === 'pending' ? `Pending (${pendingSubmissions.length})`
              : tab === 'graded' ? `Graded (${gradedSubmissions.length})`
              : <span className="flex items-center gap-1"><Award size={11} /> Student Grades</span>}
          </button>
        ))}
      </div>

      {/* Student Grades panel */}
      {activeTab === 'student_grades' && (
        <div className="pt-2">
          <div className="cinema-section-header">
            <h2 className="cinema-section-title flex items-center gap-2">
              <TrendingUp size={14} className="text-cinema-glow" /> Student Grade Summary
            </h2>
            <span className="cinema-label">{studentGrades.length} student{studentGrades.length !== 1 ? 's' : ''}</span>
          </div>
          {loadingGrades ? <LoadingSpinner className="py-10" /> : studentGrades.length === 0 ? (
            <div className="cinema-empty">
              <Award size={32} className="mx-auto mb-3 text-cinema-muted/20" />
              <p className="font-sans text-sm text-cinema-muted">No active students in this course</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/6">
                    <th className="px-2 py-2.5 text-left cinema-label font-normal">Student</th>
                    <th className="px-3 py-2.5 text-right cinema-label font-normal">Submitted</th>
                    <th className="px-3 py-2.5 text-right cinema-label font-normal">Graded</th>
                    <th className="px-3 py-2.5 text-right cinema-label font-normal">Score</th>
                    <th className="px-2 py-2.5 cinema-label font-normal">Overall</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/4">
                  {studentGrades.map(s => (
                    <tr key={s.student_id} className="hover:bg-white/[0.02] transition-colors">
                      <td className="px-2 py-3">
                        <p className="font-sans text-xs sm:text-sm text-white font-medium">{s.first_name} {s.last_name}</p>
                        <p className="font-sans text-xs text-cinema-muted">{s.student_number}</p>
                      </td>
                      <td className="px-3 py-3 text-right font-sans text-xs sm:text-sm text-cinema-muted">{s.submitted} / {s.total_assignments}</td>
                      <td className="px-3 py-3 text-right font-sans text-xs sm:text-sm text-cinema-muted">{s.graded} / {s.total_assignments}</td>
                      <td className="px-3 py-3 text-right font-sans text-xs sm:text-sm text-white">
                        {s.graded > 0 ? `${s.earned} / ${s.possible}` : <span className="text-cinema-muted">—</span>}
                      </td>
                      <td className="px-2 py-3 min-w-[140px]">
                        {s.percentage !== null ? (
                          <div className="flex items-center gap-2">
                            <div className="flex-1 bg-white/5 h-1.5">
                              <div className={`h-1.5 ${gradeAccent(s.percentage).bar}`} style={{ width: `${s.percentage}%` }} />
                            </div>
                            <span className={`font-sans text-xs sm:text-sm font-bold w-10 text-right ${gradeAccent(s.percentage).text}`}>{s.percentage}%</span>
                          </div>
                        ) : <span className="font-sans text-xs text-cinema-muted">No grades</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Submission list */}
      {activeTab !== 'student_grades' && (
        <div>
          {displayed.length === 0 ? (
            <div className="cinema-empty">
              <CheckCircle size={32} className="mx-auto mb-3 text-cinema-muted/20" />
              <p className="font-sans text-base text-cinema-muted">
                {activeTab === 'pending' ? 'No pending submissions' : 'No graded submissions yet'}
              </p>
            </div>
          ) : (
            <div className="cinema-list">
              {displayed.map((submission) => {
                const studentName = submission.student_name || `${submission.first_name} ${submission.last_name}`
                const isInPerson = submission.submission_type === 'in_person'
                return (
                  <div key={submission.id} className="cinema-list-row flex-col !items-stretch py-4">
                    <div className="flex flex-wrap justify-between items-start gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-1.5">
                          <p className="font-sans text-sm sm:text-base text-white font-medium truncate">{submission.assignment_title}</p>
                          <SubmissionTypeBadge type={submission.submission_type} />
                        </div>
                        <p className="font-sans text-xs sm:text-sm text-cinema-muted">Student: <span className="text-white font-medium">{studentName}</span></p>
                        <p className="font-sans text-xs text-cinema-muted mt-0.5">
                          {isInPerson ? 'Submitted in person'
                            : submission.submission_url
                              ? <a href={submission.submission_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-cinema-glow hover:underline"><ExternalLink size={12} /> View submission</a>
                              : submission.submission_text ? 'Text submission' : 'No file attached'}
                        </p>
                        {isInPerson && submission.submission_text && (
                          <p className="font-sans text-xs text-cinema-muted/80 mt-0.5 italic">Notes: {submission.submission_text}</p>
                        )}
                        {submission.score !== null && (
                          <p className="font-sans text-xs sm:text-sm text-green-400 font-medium mt-1">Score: {submission.score} / {submission.max_score}</p>
                        )}
                        <p className="font-sans text-xs text-cinema-muted/70 mt-1">
                          {submission.submitted_at ? new Date(submission.submitted_at).toLocaleString() : 'Date unknown'}
                        </p>
                      </div>
                      <button
                        onClick={() => {
                          setGradingSubmission(submission)
                          setGradeForm({ score: submission.score ?? '', feedback: submission.feedback ?? '' })
                          setReviewData(null)
                          setReviewError(null)
                          setFocusArea('')
                        }}
                        className={activeTab === 'pending' ? 'cinema-btn-primary text-xs sm:text-sm px-4 py-2 min-h-0' : 'cinema-btn-ghost text-xs sm:text-sm px-4 py-2 min-h-0'}
                      >
                        {activeTab === 'pending' ? 'Grade Now' : 'Edit Grade'}
                      </button>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}

      {/* Grade modal */}
      {gradingSubmission && (
        <div className={modalOverlay}>
          <div className={modalBox}>
            <div className="flex justify-between items-start">
              <h3 className="font-display text-base text-white font-light">Grade Submission</h3>
              <button onClick={closeGradingModal} className="text-cinema-muted hover:text-white transition-colors"><X size={16} /></button>
            </div>
            <div className="cinema-glass border border-white/8 rounded-sm p-4 space-y-1.5">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-sans text-sm text-white font-medium">{gradingSubmission.assignment_title}</p>
                <SubmissionTypeBadge type={gradingSubmission.submission_type} />
              </div>
              <p className="font-sans text-xs text-cinema-muted">Student: <span className="text-white">{gradingSubmission.student_name || `${gradingSubmission.first_name} ${gradingSubmission.last_name}`}</span></p>
              {gradingSubmission.submission_type === 'in_person'
                ? <p className="font-sans text-xs text-purple-400">Submitted in person — no file to view</p>
                : gradingSubmission.submission_url
                  ? <a href={gradingSubmission.submission_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-sans text-xs text-cinema-glow hover:underline"><ExternalLink size={11} /> Open submitted file</a>
                  : gradingSubmission.submission_text
                    ? <div className="p-3 cinema-glass border border-white/8 font-sans text-xs text-cinema-soft whitespace-pre-wrap max-h-28 overflow-y-auto">{gradingSubmission.submission_text}</div>
                    : null}
              {gradingSubmission.submission_type === 'in_person' && gradingSubmission.submission_text && (
                <p className="font-sans text-xs text-cinema-muted italic">Notes: {gradingSubmission.submission_text}</p>
              )}
            </div>

            <form onSubmit={handleGradeSubmit} className="space-y-4">
              <div>
                <label className="cinema-label block mb-1.5">Score <span className="font-normal">(out of {gradingSubmission.max_score})</span></label>
                <input type="number" min="0" max={gradingSubmission.max_score} value={gradeForm.score}
                  onChange={(e) => setGradeForm({ ...gradeForm, score: e.target.value })} className={inputCls} required />
              </div>

              {/* AI-010 Review Assist Section */}
              <div className="border-t border-white/8 pt-3 space-y-3">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div>
                    <span className="cinema-label block">Review Assistance</span>
                    <span className="font-sans text-[11px] text-cinema-muted">Non-binding observations & feedback drafting</span>
                  </div>
                  <button
                    type="button"
                    onClick={handleAiReviewAssist}
                    disabled={reviewLoading}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-sans font-medium text-cinema-glow bg-cinema-glow/10 hover:bg-cinema-glow/20 border border-cinema-glow/30 transition-colors rounded-sm disabled:opacity-50"
                  >
                    {reviewLoading ? (
                      <>
                        <LoadingSpinner size="sm" />
                        <span>Analyzing Evidence...</span>
                      </>
                    ) : (
                      <>
                        <Sparkles size={12} />
                        <span>AI Review Assist</span>
                      </>
                    )}
                  </button>
                </div>

                {reviewError && (
                  <div className="p-2.5 bg-amber-500/10 border border-amber-500/20 text-amber-300 font-sans text-xs">
                    {reviewError}
                  </div>
                )}

                {reviewData && (
                  <div className="space-y-3 border border-white/8 p-3.5 bg-black/20 text-xs font-sans">
                    {/* Summary */}
                    {reviewData.review_summary && (
                      <div>
                        <div className="cinema-label mb-1">Review Summary</div>
                        <p className="text-cinema-soft leading-relaxed">{reviewData.review_summary}</p>
                      </div>
                    )}

                    {/* Deliverable Checklist */}
                    {Array.isArray(reviewData.deliverable_checklist) && reviewData.deliverable_checklist.length > 0 && (
                      <div className="border-t border-white/6 pt-2.5">
                        <div className="cinema-label mb-1.5">Deliverables Checklist</div>
                        <div className="space-y-1.5">
                          {reviewData.deliverable_checklist.map((item, idx) => (
                            <div key={idx} className="flex items-start justify-between gap-2 border-b border-white/4 pb-1.5">
                              <div className="min-w-0 flex-1">
                                <span className="text-white font-medium">{item.deliverable_name || item.requirement}</span>
                                {item.evidence && <p className="text-cinema-muted/80 text-[11px] mt-0.5">{item.evidence}</p>}
                                {item.uncertainty_note && <p className="text-amber-400/80 text-[11px] italic mt-0.5">{item.uncertainty_note}</p>}
                              </div>
                              <StatusBadge status={item.status} />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Observable Strengths & Gaps */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 border-t border-white/6 pt-2.5">
                      {Array.isArray(reviewData.key_strengths) && reviewData.key_strengths.length > 0 && (
                        <div>
                          <div className="cinema-label mb-1 text-green-400">Observable Strengths</div>
                          <ul className="space-y-1 list-disc list-inside text-cinema-soft">
                            {reviewData.key_strengths.map((s, idx) => (
                              <li key={idx}>
                                {typeof s === 'string' ? s : (
                                  <>
                                    <span className="text-white">{s.item || s.strength}</span>
                                    {s.evidence && <span className="text-cinema-muted">: {s.evidence}</span>}
                                  </>
                                )}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {Array.isArray(reviewData.areas_for_improvement) && reviewData.areas_for_improvement.length > 0 && (
                        <div>
                          <div className="cinema-label mb-1 text-amber-400">Areas for Improvement</div>
                          <ul className="space-y-1 list-disc list-inside text-cinema-soft">
                            {reviewData.areas_for_improvement.map((g, idx) => (
                              <li key={idx}>
                                {typeof g === 'string' ? g : (
                                  <>
                                    <span className="text-white">{g.item || g.gap}</span>
                                    {g.evidence && <span className="text-cinema-muted">: {g.evidence}</span>}
                                  </>
                                )}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>

                    {/* Uncertainties */}
                    {Array.isArray(reviewData.uncertainties) && reviewData.uncertainties.length > 0 && (
                      <div className="border-t border-white/6 pt-2">
                        <div className="cinema-label mb-1 text-cinema-muted">Uncertainties (Manual Inspection Advised)</div>
                        <ul className="space-y-0.5 list-disc list-inside text-cinema-muted/90 text-[11px]">
                          {reviewData.uncertainties.map((u, idx) => (
                            <li key={idx}>{u}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Draft Feedback Preview & Insertion */}
                    {reviewData.draft_feedback && (
                      <div className="border-t border-white/8 pt-2.5 space-y-2">
                        <div className="flex items-center justify-between gap-2">
                          <span className="cinema-label text-cinema-glow">Draft Feedback (Editable)</span>
                          <button
                            type="button"
                            onClick={() => handleInsertFeedback(reviewData.draft_feedback)}
                            className="cinema-btn-ghost text-xs px-2.5 py-1 min-h-0 text-cinema-glow border-cinema-glow/30 hover:bg-cinema-glow/10 flex items-center gap-1"
                          >
                            <ArrowDownLeft size={12} />
                            <span>Insert into Feedback</span>
                          </button>
                        </div>
                        <div className="p-2.5 bg-white/4 border border-white/6 font-sans text-xs text-cinema-soft whitespace-pre-wrap max-h-32 overflow-y-auto">
                          {reviewData.draft_feedback}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div>
                <label className="cinema-label block mb-1.5">Feedback</label>
                <textarea value={gradeForm.feedback} onChange={(e) => setGradeForm({ ...gradeForm, feedback: e.target.value })}
                  className={inputCls} rows="4" placeholder="Provide feedback to the student..." />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="submit" className="cinema-btn-primary text-xs px-5 py-2.5 min-h-0 flex items-center gap-2"><Save size={13} /> Submit Grade</button>
                <button type="button" onClick={closeGradingModal} className="cinema-btn-ghost text-xs px-4 py-2.5 min-h-0">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* In-Person modal */}
      {showInPersonModal && (
        <div className={modalOverlay}>
          <div className={modalBox}>
            <div className="flex justify-between items-start">
              <div>
                <h3 className="font-display text-lg text-white font-light">Record In-Person Submission</h3>
                <p className="font-sans text-xs sm:text-sm text-cinema-muted mt-0.5">Log a physical assignment hand-in by a student</p>
              </div>
              <button onClick={() => setShowInPersonModal(false)} className="text-cinema-muted hover:text-white transition-colors"><X size={16} /></button>
            </div>
            <form onSubmit={handleInPersonSubmit} className="space-y-4">
              <div>
                <label className="cinema-label block mb-1.5">Student <span className="text-red-400">*</span></label>
                <select value={inPersonForm.student_id} onChange={(e) => setInPersonForm({ ...inPersonForm, student_id: e.target.value })} className={inputCls} required>
                  <option value="" className="bg-gray-900">— Select student —</option>
                  {courseStudents.map(s => <option key={s.id} value={s.id} className="bg-gray-900">{s.first_name} {s.last_name} ({s.student_number})</option>)}
                </select>
              </div>
              <div>
                <label className="cinema-label block mb-1.5">Assignment <span className="text-red-400">*</span></label>
                <select value={inPersonForm.assignment_id} onChange={(e) => setInPersonForm({ ...inPersonForm, assignment_id: e.target.value })} className={inputCls} required>
                  <option value="" className="bg-gray-900">— Select assignment —</option>
                  {courseAssignments.map(a => <option key={a.id} value={a.id} className="bg-gray-900">{a.title}{a.lesson_title ? ` — ${a.lesson_title}` : ''} (Max: {a.max_score})</option>)}
                </select>
              </div>
              <div>
                <label className="cinema-label block mb-1.5">Notes <span className="text-cinema-muted font-normal">(optional)</span></label>
                <textarea value={inPersonForm.notes} onChange={(e) => setInPersonForm({ ...inPersonForm, notes: e.target.value })}
                  className={inputCls} rows="2" placeholder="e.g. Printed portfolio submitted..." />
              </div>
              <div className="border-t border-white/8 pt-4">
                <p className="font-sans text-xs text-white font-medium mb-3">Grade Now <span className="text-cinema-muted font-normal">(optional — can grade later from Pending tab)</span></p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="cinema-label block mb-1.5">Score
                      {inPersonForm.assignment_id && courseAssignments.find(a => String(a.id) === String(inPersonForm.assignment_id)) && (
                        <span className="font-normal ml-1">(out of {courseAssignments.find(a => String(a.id) === String(inPersonForm.assignment_id))?.max_score})</span>
                      )}
                    </label>
                    <input type="number" min="0" max={courseAssignments.find(a => String(a.id) === String(inPersonForm.assignment_id))?.max_score || undefined}
                      value={inPersonForm.score} onChange={(e) => setInPersonForm({ ...inPersonForm, score: e.target.value })}
                      className={inputCls} placeholder="Leave blank to grade later" />
                  </div>
                  <div>
                    <label className="cinema-label block mb-1.5">Feedback</label>
                    <input type="text" value={inPersonForm.feedback} onChange={(e) => setInPersonForm({ ...inPersonForm, feedback: e.target.value })}
                      className={inputCls} placeholder="Optional" />
                  </div>
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <button type="submit" disabled={savingInPerson} className="cinema-btn-primary text-xs px-5 py-2.5 min-h-0 flex items-center gap-2 disabled:opacity-50">
                  <FileText size={13} /> {savingInPerson ? 'Saving...' : inPersonForm.score !== '' ? 'Record & Grade' : 'Record Submission'}
                </button>
                <button type="button" onClick={() => setShowInPersonModal(false)} className="cinema-btn-ghost text-xs px-4 py-2.5 min-h-0">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
